import React, {useState, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import useAppContext from '../../contexts/UseAppContext';
import DataLoader from '../../components/DataLoader/DataLoader';
import DataError from '../../components/DataError/DataError';
import Button from '../../components/Button/Button';
import CheckoutLinkForm from '../../components/CheckoutLinkForm/CheckoutLinkForm';
import Activity from '../../helpers/Activity';
import ExternalAnalytics from '../../helpers/ExternalAnalytics';
import Navigation from '../../config/Navigation';
import WebService from '../../helpers/WebService';
import Debug from '../../helpers/Debug';
import {firestore, getCollectionPath} from '../../config/Firebase';
import {doc, onSnapshot} from 'firebase/firestore';
import {storeConverter} from '../../models/Store';
import './AddressCopy.css';

function AddressCopy({section}) {
    const navigate = useNavigate();
    const context = useAppContext();
    const shoppingCart = context.shoppingCart;
    const storeId = context.store;
    const urlParams = useParams();
    const clientType = urlParams.clientType;
    const [isLoading, setIsLoading] = useState(true);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [store, setStore] = useState(undefined);
    const [checkoutData, setCheckoutData] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [copied, setCopied] = useState(false);
    let unsubscribeStore = undefined;

    useEffect(() => {
        getStore();
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getStore = () => {
        if (!shoppingCart || !shoppingCart.items || shoppingCart.items.length === 0) {
            onLoadError('S&G-empty');
            return;
        }
        const _path = getCollectionPath('stores');
        const _doc = doc(firestore, _path, storeId).withConverter(storeConverter);
        unsubscribeStore = onSnapshot(_doc, (_document) => {
            try {
                const exists = _document.exists();
                if (!exists) throw new Error('S&G-no-store');
                const _store = _document.data();
                onStoreLoadSuccess(_store);
            } catch (error) {
                const message = error.message;
                Debug.printToLog('error', error);
                onLoadError(message.includes('S&G') ? message : 'S&G-store-init-error');
            }
        }, (error) => {
            Debug.printToLog('error', error);
            onLoadError('S&G-store-load-error');
        });
    };

    const unsubscribe = () => {
        if (unsubscribeStore) unsubscribeStore();
    };

    const onStoreLoadSuccess = (_store) => {
        setStore(_store);
        setError(undefined);
    };

    useEffect(() => {
        if (store) {
            const address = store.address;
            if (!address) onLoadError('S&G-no-address', undefined);
            else {
                const isFirstLoad = isLoading && !dataLoaded;
                if (isFirstLoad) getCheckoutData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store]);

    const getCheckoutData = () => {
        WebService.getCheckoutData(clientType, context.shoppingCart)
            .then((_checkoutData) => {
                if (_checkoutData) onCheckoutLoadSuccess(_checkoutData);
                else onLoadError('S&G-checkout-load-error');
            });
    };

    const onCheckoutLoadSuccess = (_checkoutData) => {
        setCheckoutData(_checkoutData);
        setError(undefined);
        setDataLoaded(true);
        setIsLoading(false);
    };

    const onLoadError = (type) => {
        setStore(undefined);
        setCheckoutData(undefined);
        setError(type);
        setDataLoaded(false);
        setIsLoading(false);
    };

    useEffect(() => {
        if (!isLoading && dataLoaded) {
            const source = shoppingCart && shoppingCart.hasItems() ? shoppingCart.items[0] : undefined;
            ExternalAnalytics.load(source, section, 'page', 'carga');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, dataLoaded]);

    const getMetadata = (additionalData, customQuantity) => {
        const metadata = {shopping_cart: shoppingCart ? shoppingCart.toActivityLog(customQuantity) : null}; // This is a snapshot of the contents of the cart when an action is performed, it's not the current state.
        if (additionalData) return {...metadata, ...additionalData};
        else return metadata;
    };

    const toHome = (error) => {
        Activity.log('address-copy-view', 'scan-button', 'click', getMetadata({error: error || null}));
        const url = Navigation.getHomeUrl();
        navigate(url);
    };

    const reload = (error) => {
        Activity.log('address-copy-view', 'reload-button', 'click', getMetadata({error: error || null}))
            .then(() => {
                Navigation.reload();
            });
    };

    const copyAddress = () => {
        try {
            navigator.clipboard.writeText(store.address);
            Activity.log('address-copy-view', 'copy-button', 'click', getMetadata({store_address: store.address}));
        } catch (error) {
            Debug.printToLog('error', error);
        }
        setCopied(true);
    };

    const getRedirectUrl = (url) => {
        if (url) return `${url}?oda=${store.id}&omc=qr_autoatencion`;
        else return undefined;
    };

    const onSubmit = () => {
        const sources = shoppingCart && shoppingCart.hasItems() ? shoppingCart.items : undefined;
        ExternalAnalytics.addShippingInfo(sources, section, store.address);
        Activity.log('address-copy-view', 'next-button', 'click', getMetadata({store_address: store.address}));
    };

    const wrapperStyle = () => {
        return {
            marginBottom: context.addressCopyNextButtonHeight
        };
    };

    const addressContentStyle = () => {
        return {
            borderColor: copied ? '#45AB19' : '#002EFF',
        };
    };

    const renderLoader = () => {
        return (
            <div className='address-copy_data-loader'>
                <DataLoader message='Obteniendo información de la tienda e iniciando el proceso de checkout...' loaderSize={undefined}/>
            </div>
        );
    };

    const renderError = () => {
        let message;
        let actionLabel;
        let action;
        switch (error) {
            case 'S&G-empty':
                message = 'El carro de compras está vacío';
                actionLabel = 'Escanear un producto';
                action = () => toHome(error);
                break;
            case 'S&G-no-store':
                message = 'No hemos encontrado la tienda especificada';
                actionLabel = 'Escanear otro producto';
                action = () => toHome(error);
                break;
            case 'S&G-no-address':
                message = 'La dirección de la tienda no es válida';
                actionLabel = 'Escanear otro producto';
                action = () => toHome(error);
                break;
            case 'S&G-checkout-load-error':
                message = 'Se ha producido un error al iniciar el proceso de checkout';
                actionLabel = 'Reintentar';
                action = () => reload(error);
                break;
            case 'S&G-store-load-error':
                message = 'Se ha producido un error al obtener la información de la tienda';
                actionLabel = 'Reintentar';
                action = () => reload(error);
                break;
            case 'S&G-store-init-error':
                message = 'Se ha producido un error al cargar la tienda';
                actionLabel = 'Reintentar';
                action = () => reload(error);
                break;
            default:
                break;
        }
        if (message) {
            return (
                <div className='address-copy_data-error'>
                    <DataError message={message} actionLabel={actionLabel} action={action} padding={20} maxWidth={undefined} iconSize={undefined} fontSize={undefined}/>
                </div>
            );
        } else return undefined;
    };

    const renderContent = () => {
        return (
            <div className='address-copy_wrapper' style={wrapperStyle()}>
                <div className='address-copy_title'>Por favor, copia esta dirección para la entrega de tu compra.</div>
                <div className='address-copy_address-content' style={addressContentStyle()}>{store.address}</div>
                <div className='address-copy_copy-button'>
                    <Button
                        icon={undefined}
                        label={copied ? 'Copiado' : 'Copiar'}
                        labelColor={copied ? '#45AB19' : '#FFFFFF'}
                        backgroundColor={copied ? '#FFFFFF' : '#FF3D00'}
                        borderColor={copied ? '#45AB19' : '#FF3D00'}
                        borderWidth={0}
                        borderRadius={20}
                        padding='10px 35px'
                        paddingTarget='label'
                        margin={0}
                        width='auto'
                        minWidth={undefined}
                        maxWidth={undefined}
                        height='auto'
                        minHeight={undefined}
                        maxHeight={undefined}
                        transition='container'
                        fontSize={18}
                        boxShadow='unset'
                        isLoading={false}
                        loadLabel={undefined}
                        disabled={false}
                        showPulseEffect={false}
                        notificationNumber={undefined}
                        notificationBackgroundColor={undefined}
                        notificationLabelColor={undefined}
                        onClick={copyAddress}
                    />
                </div>
                <div className='address-copy_footer'>
                    <div className='address-copy_buttons'>
                        <div className='address-copy_button'>
                            <Button
                                icon={undefined}
                                label='Siguiente'
                                labelColor='#FFFFFF'
                                backgroundColor='#002EFF'
                                borderColor='#002EFF'
                                borderWidth={0}
                                borderRadius={0}
                                padding='15px 10px'
                                paddingTarget='label'
                                margin={0}
                                width='100%'
                                minWidth={undefined}
                                maxWidth={undefined}
                                height={context.addressCopyNextButtonHeight}
                                minHeight={undefined}
                                maxHeight={undefined}
                                transition='label'
                                fontSize={18}
                                boxShadow='unset'
                                isLoading={false}
                                loadLabel={undefined}
                                disabled={!copied}
                                showPulseEffect={false}
                                notificationNumber={undefined}
                                notificationBackgroundColor={undefined}
                                notificationLabelColor={undefined}
                                onClick={undefined}
                            >
                                <CheckoutLinkForm
                                    url={getRedirectUrl(checkoutData.url)}
                                    method={checkoutData.method}
                                    inputs={checkoutData.inputs}
                                    customSubmitLabel='Siguiente'
                                    disabled={!copied}
                                    onSubmit={onSubmit}
                                />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='address-copy'>
            {isLoading && renderLoader()}
            {!isLoading && !dataLoaded && renderError()}
            {!isLoading && dataLoaded && renderContent()}
        </div>
    );
}

export default AddressCopy;